/**
 * Client-side mirror of the backend's `validationPattern` check
 * (`apps/assets-api/src/taxonomy/lib/field-pattern.ts`).
 *
 * This exists so the builder can flag a violating value while it is being
 * AUTHORED rather than letting it surface as a 400 at generation time. The two
 * implementations MUST agree — a grid that flags values the API accepts, or
 * stays quiet on ones it rejects, is worse than no warning, because it teaches
 * people to ignore it. Every rule below is deliberately identical to the
 * server's:
 *
 *   - full-match semantics, wrapped as `^(?:…)$` so a top-level alternation
 *     (`A|B`) can't bind the anchor to a single branch
 *   - an empty value is exempt (presence is `required`'s job)
 *   - a pattern that doesn't compile is treated as NO constraint, not a failure
 *   - values beyond the length cap are refused without being tested
 *
 * THE ENGINE IS PART OF THAT LIST. Both sides run RE2, via `re2js`, and that is
 * load-bearing twice over. Swapping this file back to the platform's `RegExp`
 * would reintroduce both problems at once:
 *
 *   1. RE2 matches in time linear in the input; `RegExp` backtracks. An admin
 *      can store `([a-z]|[a-z])+`, which the API is right to accept because
 *      nothing on its side can blow up — but under a backtracking engine that
 *      pattern takes minutes against a 35-character value, and this module runs
 *      on the render path for every value cell, including for read-only
 *      viewers. One stored pattern would freeze the tab for everyone.
 *   2. The two engines do not agree on what they MATCH even when both compile
 *      the same source. RE2's `\s` is ASCII-only where the platform's includes
 *      NBSP and the Unicode spaces, and RE2's `.` matches CR where the
 *      platform's does not. A value pasted out of a spreadsheet is exactly how
 *      an NBSP arrives, and the disagreement is silent in both directions: a
 *      cell the grid calls clean that the API then rejects, and a cell the grid
 *      reddens that the API is fine with.
 *
 * Compiling with the same engine is what makes "the two agree" true by
 * construction rather than by inspection. `IsEnforceablePattern` on the API is
 * a separate question — what may be STORED, mirrored below by
 * {@link isCompilableFieldPattern} — and it was never enough on its own to make
 * the two sides mean the same thing, which is the mistake this replaced.
 *
 * The length cap is no longer anyone's ReDoS defence. It stays because a naming
 * token that long is already wrong, and refusing it costs nothing.
 */
import { RE2JS } from 're2js';
import { i18n } from '@/shared/i18n';

/** Mirrors `MAX_TESTABLE_VALUE_LENGTH` on the backend. */
export const MAX_TESTABLE_VALUE_LENGTH = 512;

const MAX_CACHE_ENTRIES = 200;
const compiled = new Map<string, RE2JS | null>();

function compilePattern(pattern: string): RE2JS | null {
  const cached = compiled.get(pattern);
  if (cached !== undefined) return cached;

  let regex: RE2JS | null;
  try {
    regex = RE2JS.compile(`^(?:${pattern})$`);
  } catch {
    regex = null;
  }

  if (compiled.size >= MAX_CACHE_ENTRIES) compiled.clear();
  compiled.set(pattern, regex);
  return regex;
}

/**
 * True when the API would accept this pattern for STORAGE.
 *
 * Deliberately a different question from "can we match with it", and answered
 * with a different rule. Matching is RE2 alone, above. Storability mirrors
 * `IsEnforceablePattern` on the API, which requires RE2 to compile the pattern
 * AND the platform's `RegExp` to parse it in strict unicode mode.
 *
 * Both halves are load-bearing here, and dropping either reintroduces the bug
 * this replaced — a modal that calls a pattern valid and then collects a 400 on
 * save. RE2 alone accepts `[[:alpha:]]+` and `x\z`, which the API refuses;
 * `RegExp` alone accepts lookahead and backreferences, which the API also
 * refuses. The `new RegExp` below is a PARSE CHECK and nothing else — it must
 * never be used to match a value, which is the whole reason this module runs
 * RE2.
 */
export function isCompilableFieldPattern(pattern: string): boolean {
  if (compilePattern(pattern) === null) return false;
  try {
    // eslint-disable-next-line i18next/no-literal-string -- RegExp flag
    new RegExp(`^(?:${pattern})$`, 'u');
    return true;
  } catch {
    return false;
  }
}

/**
 * True when `value` satisfies `pattern`. An absent pattern, an empty value, or
 * an uncompilable pattern all count as satisfied.
 */
export function matchesFieldPattern(value: string, pattern: string | null | undefined): boolean {
  if (!pattern?.trim()) return true;
  if (value === '') return true;
  if (value.length > MAX_TESTABLE_VALUE_LENGTH) return false;

  const regex = compilePattern(pattern);
  if (!regex) return true;
  return regex.matcher(value).matches();
}

/**
 * Human-readable reason a value fails, or null when it passes. Used verbatim
 * as the cell tooltip, so it has to read as guidance rather than a stack trace.
 */
export function fieldPatternViolation(
  value: string,
  pattern: string | null | undefined,
): string | null {
  if (matchesFieldPattern(value, pattern)) return null;
  return value.length > MAX_TESTABLE_VALUE_LENGTH
    ? i18n.t('knowledge:taxonomy.fieldPattern.tooLong', {
        cap: MAX_TESTABLE_VALUE_LENGTH,
      })
    : i18n.t('knowledge:taxonomy.fieldPattern.doesNotMatchPattern', {
        pattern: pattern ?? '',
      });
}
